/**
 * Build readiness — the registry gate every EAS build of a school app waits on (SCH-2318).
 *
 * GET /v1/mobile-apps/:id/build-readiness?platform=IOS|ANDROID[&environment=]   (super-admin)
 *
 * One read-only check per thing a build needs from outside the repo, each derived from a build that
 * shipped without it: the stored registry preconditions (`registry.<field>`, judged with the shared
 * missing rule), both Firebase config files in the EAS environment (a build with only the JSON rang
 * on iOS but could never answer), the OneSignal app, its REST key and its channel for this bundle
 * (APNs key, bundle and environment — a non-production environment is a warning — or FCM transport
 * and project), the App Group on every identifier the build signs, the APNs and submit keys, and
 * the EAS environment agreeing with the registry. The OneSignal configuration checks are the one
 * set `sj onesignal` reads too (SCH-2963).
 *
 * `ready` is false when any `blocking` check is not `ok`. A check that is neither ok nor blocking is
 * a warning: worth reading, stops nothing. Every build dispatch refuses while `ready` is false.
 */

import { z } from 'zod'

export const BuildReadinessPlatformSchema = z.enum(['IOS', 'ANDROID'])
export type BuildReadinessPlatform = z.infer<typeof BuildReadinessPlatformSchema>

export const BuildReadinessCheckSchema = z.object({
  id: z.string(),
  label: z.string(),
  ok: z.boolean(),
  /** A blocking check that is not ok stops every build; a non-blocking one is a warning. */
  blocking: z.boolean(),
  /** What was observed, and what to do when it is not ok — never a secret. */
  detail: z.string()
})
export type BuildReadinessCheck = z.infer<typeof BuildReadinessCheckSchema>

export const BuildReadinessReportSchema = z.object({
  ready: z.boolean(),
  platform: BuildReadinessPlatformSchema,
  checkedAt: z.string(),
  checks: z.array(BuildReadinessCheckSchema)
})
export type BuildReadinessReport = z.infer<typeof BuildReadinessReportSchema>

export const GetBuildReadinessQuerySchema = z.object({
  platform: BuildReadinessPlatformSchema,
  /**
   * The EAS environment the build profile reads; store builds read `production`, the default. The
   * same three values as `EasEnvironmentSchema`, spelled out here because `./index` re-exports this
   * file and an import back from it would read the schema before it is defined.
   */
  environment: z.enum(['development', 'preview', 'production']).optional()
})
export type GetBuildReadinessQuery = z.infer<typeof GetBuildReadinessQuerySchema>

export const GetBuildReadinessResponseSchema = z.object({
  data: BuildReadinessReportSchema,
  message: z.string()
})
export type GetBuildReadinessResponse = z.infer<typeof GetBuildReadinessResponseSchema>
